import { useCallback, useEffect, useState } from "react";
import { Form, Formik, useFormikContext, type FormikHelpers } from "formik";
import Button from "../UI/Button";
import FormikSubmitButton from "../UI/FormikSubmitButton";
import Text from "../UI/Text";
import Callout from "../UI/Callout";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../UI/ModalDialog";
// @ts-expect-error pure js
import Radio from "../UI/Radio";
// @ts-expect-error pure js
import Checkbox from "../UI/Checkbox";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
// @ts-expect-error pure js
import useTenants from "../../hooks/use-tenants";
import type { Tenant } from "../../models/tenant";
import styles from "./BulkAvisEcheanceModal.module.css";
import bulkStyles from "./Bulk/BulkActions.module.css";
import BulkSelectionCard, {
  BulkSelectionCardRow,
} from "./Bulk/BulkSelectionCard";
import BulkPdfPreview from "./Bulk/BulkPdfPreview";
import { usePdfPreviewCache } from "./Bulk/usePdfPreviewCache";
import type { BulkResult, BulkTenantModalProps } from "./Bulk/types";
import { useBulkResultsToast } from "./Bulk/useBulkResultsToast";
import { useBatchSelection } from "./Bulk/useBatchSelection";

type Periode = "current" | "next";

interface BulkAvisEcheanceFormValues {
  batchIds: number[];
  periode: Periode;
  sendEmail: boolean;
}

export default function BulkAvisEcheanceModal({
  open,
  onOpenChange,
  selectedTenantIds,
  resetSelection,
}: BulkTenantModalProps) {
  return (
    <ModalDialog
      open={open}
      onOpenChange={onOpenChange}
      size="xl"
      header="Générer des avis d'échéance"
    >
      <BulkAvisEcheanceContent
        close={() => onOpenChange(false)}
        selectedTenantIds={selectedTenantIds}
        resetSelection={resetSelection}
      />
    </ModalDialog>
  );
}

interface BulkAvisEcheanceContentProps {
  close: () => void;
  selectedTenantIds: number[];
  resetSelection?: () => void;
}

function BulkAvisEcheanceContent({
  close,
  selectedTenantIds,
  resetSelection,
}: BulkAvisEcheanceContentProps) {
  const {
    previewAvisEcheancePdf,
    bulkCreateAvisEcheance,
    bulkComputeAvisEcheance,
    tenantsById,
  }: {
    previewAvisEcheancePdf: (params: {
      tenantId: number;
      periode: Periode;
    }) => Promise<string>;
    bulkCreateAvisEcheance: (params: {
      tenantIds: number[];
      periode: Periode;
      sendEmail: boolean;
    }) => Promise<{ results: BulkResult[] }>;
    bulkComputeAvisEcheance: (params: {
      tenantIds: number[];
      periode: Periode;
    }) => Promise<{ results: BulkResult[] }>;
    tenantsById: Record<number, Tenant>;
  } = useTenants({ withById: true });

  const notifyBulkResults = useBulkResultsToast(tenantsById);

  async function handleSubmit(
    values: BulkAvisEcheanceFormValues,
    { setSubmitting, setStatus }: FormikHelpers<BulkAvisEcheanceFormValues>,
  ) {
    setStatus(undefined);
    try {
      const response = await bulkCreateAvisEcheance({
        tenantIds: values.batchIds,
        periode: values.periode,
        sendEmail: values.sendEmail,
      });
      notifyBulkResults(
        response.results,
        ({ successCount, failureCount, total }) => {
          const s = successCount > 1 ? "s" : "";
          const sf = failureCount > 1 ? "s" : "";
          return {
            successSection: `Document${s} généré${s}${
              values.sendEmail ? " et envoyé par mail" : ""
            }`,
            failureSection: `Document${sf} non généré${sf}`,
            successToastTitle:
              failureCount === 0
                ? `${successCount} avis d'échéance généré${s}`
                : `${successCount} avis sur ${total} généré${s}`,
            errorToastTitle: "Aucun document généré",
          };
        },
      );
      resetSelection?.();
      close();
    } catch (e) {
      setStatus(handleAPIError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Formik<BulkAvisEcheanceFormValues>
      initialValues={{
        batchIds: selectedTenantIds,
        periode: "next",
        sendEmail: false,
      }}
      onSubmit={handleSubmit}
    >
      <Form>
        <BulkAvisEcheanceFields
          previewAvisEcheancePdf={previewAvisEcheancePdf}
          bulkComputeAvisEcheance={bulkComputeAvisEcheance}
          tenantsById={tenantsById}
          selectedTenantIds={selectedTenantIds}
          close={close}
        />
      </Form>
    </Formik>
  );
}

interface BulkAvisEcheanceFieldsProps {
  previewAvisEcheancePdf: (params: {
    tenantId: number;
    periode: Periode;
  }) => Promise<string>;
  bulkComputeAvisEcheance: (params: {
    tenantIds: number[];
    periode: Periode;
  }) => Promise<{ results: BulkResult[] }>;
  tenantsById: Record<number, Tenant>;
  selectedTenantIds: number[];
  close: () => void;
}

function BulkAvisEcheanceFields({
  previewAvisEcheancePdf,
  bulkComputeAvisEcheance,
  tenantsById,
  selectedTenantIds,
  close,
}: BulkAvisEcheanceFieldsProps) {
  const { values, status } = useFormikContext<BulkAvisEcheanceFormValues>();
  const { periode } = values;

  const {
    batchIds,
    batchTenants,
    activeTenantId,
    setActiveTenantId,
    removeFromBatch,
  } = useBatchSelection(tenantsById);
  const [computeByKey, setComputeByKey] = useState<Record<string, BulkResult>>(
    {},
  );

  useEffect(() => {
    if (selectedTenantIds.length === 0) return;
    let cancelled = false;
    bulkComputeAvisEcheance({ tenantIds: selectedTenantIds, periode })
      .then(({ results: computeResults }) => {
        if (cancelled) return;
        setComputeByKey((prev) => {
          const next = { ...prev };
          for (const r of computeResults) {
            next[`${r.tenant_id}-${periode}`] = r;
          }
          return next;
        });
      })
      .catch(() => {
        // Per-card display falls back to "no eligibility info" if compute fails.
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTenantIds, periode, bulkComputeAvisEcheance]);

  const activeKey = activeTenantId ? `${activeTenantId}-${periode}` : null;
  const activeCompute = activeKey ? computeByKey[activeKey] : undefined;
  const activeIsIneligible =
    activeCompute !== undefined && !activeCompute.success;

  const fetcher = useCallback(
    ({ tenantId, periode: p }: { tenantId: number; periode: Periode }) =>
      previewAvisEcheancePdf({ tenantId, periode: p }),
    [previewAvisEcheancePdf],
  );
  const { previewUrl, previewLoading, previewError } = usePdfPreviewCache({
    activeKey,
    activePayload:
      activeTenantId !== null ? { tenantId: activeTenantId, periode } : null,
    fetcher,
    enabled: !activeIsIneligible,
  });

  return (
    <>
      <ModalDialogBody className={bulkStyles.body}>
        <Text color="muted">
          Vous avez {batchIds.length} avis d'échéance à envoyer.
        </Text>

        <div className={styles.periodeRow}>
          <Text weight="medium">Période :</Text>
          <Radio name="periode" value="current" label="Période en cours" />
          <Radio name="periode" value="next" label="Période suivante" />
        </div>

        <div className={bulkStyles.split}>
          <div className={bulkStyles.cardList}>
            {batchTenants.length === 0 && (
              <Text color="muted">Aucun locataire sélectionné.</Text>
            )}
            {batchTenants.map((tenant) => {
              const compute = computeByKey[`${tenant.id}-${periode}`];
              const isIneligible = Boolean(compute && !compute.success);
              const fileName =
                tenant.is_type_quittance === false
                  ? "Facture.pdf"
                  : "Avis_d_échéance.pdf";
              const total = (tenant.loyer_hc ?? 0) + (tenant.charges ?? 0);
              return (
                <BulkSelectionCard
                  key={tenant.id}
                  fileName={fileName}
                  tenantName={tenant.nom}
                  isActive={tenant.id === activeTenantId}
                  isIneligible={isIneligible}
                  ineligibleReason={compute?.error}
                  onSelect={() => setActiveTenantId(tenant.id)}
                  onRemove={() => removeFromBatch(tenant.id)}
                >
                  <BulkSelectionCardRow label="Loyer + charges">
                    <Text as="span" weight="bold" color="muted">
                      {currencyFormatter(total)}
                    </Text>
                  </BulkSelectionCardRow>
                </BulkSelectionCard>
              );
            })}
          </div>

          <BulkPdfPreview
            iframeTitle="Aperçu avis d'échéance"
            hasSelection={activeTenantId !== null}
            isIneligible={activeIsIneligible}
            ineligibleTitle="Document impossible à générer pour ce locataire"
            ineligibleReason={activeCompute?.error}
            previewUrl={previewUrl}
            previewLoading={previewLoading}
            previewError={previewError}
          />
        </div>

        <Callout variant="danger">{status}</Callout>
      </ModalDialogBody>
      <ModalDialogFooter
        start={
          <Checkbox name="sendEmail">Envoyer ce document par mail</Checkbox>
        }
      >
        <Button variant="secondary" type="button" onClick={close}>
          Annuler
        </Button>
        <FormikSubmitButton disabled={batchIds.length === 0}>
          Générer
        </FormikSubmitButton>
      </ModalDialogFooter>
    </>
  );
}
